import { useEffect, useMemo, useRef } from "react";
import debounce from "lodash.debounce";

/**
 * This hook returns one debounced function for the lifetime of the component. It always calls the
 * latest func, so a callback that changes (for example with a new endpoint or language) is used
 * from the next call. A pending call is cancelled when the component unmounts.
 */
export default <T extends (...args: any[]) => void>(func: T, waitMs: number) => {
  const latestFunc = useRef(func);
  useEffect(() => {
    latestFunc.current = func;
  });

  const debounced = useMemo(
    () =>
      debounce(
        (...args: Parameters<T>) => latestFunc.current(...args),
        waitMs,
      ),
    [waitMs],
  );
  useEffect(() => () => debounced.cancel(), [debounced]);

  return debounced;
};
